import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import { useAuth } from "./auth/AuthContext";
import { call, db } from "./firebase";
import { useLeopardyGames } from "./hooks/useLeopardyGames";

const paperStyle = { padding: 16, borderRadius: 12, border: "1px solid #e0e0e0" };
const promptIdeas = [
  "Grade 10 physics: kinematics, energy, waves, electricity",
  "Canadian history: confederation, world wars, indigenous history, modern politics",
  "Intro Spanish vocabulary: food, travel, family, school, verbs",
  "Algebra review: linear equations, factoring, quadratics, exponents, word problems",
];
const exampleJson = JSON.stringify({
  title: "World Biomes Leopardy",
  teams: [],
  categories: [
    {
      name: "Rainforest",
      clues: [
        { value: 100, prompt: "Average temp range of tropical rainforests?", answer: "20-25°C year-round" },
        { value: 200, prompt: "Why do rainforests have thin topsoil?", answer: "Rapid decomposition and uptake by plants" },
        { value: 300, prompt: "One animal adaptation to canopy life:", answer: "Prehensile tails (e.g., spider monkeys)" },
        { value: 400, prompt: "How deforestation impacts carbon cycle?", answer: "Less CO₂ uptake, more released from burning/decay" },
        { value: 500, prompt: "Name the forest layer with little sunlight reaching ground.", answer: "Understory" }
      ]
    },
    {
      name: "Desert",
      clues: [
        { value: 100, prompt: "Annual rainfall threshold for deserts?", answer: "Less than ~25 cm" },
        { value: 200, prompt: "Adaptation of camels for water storage:", answer: "Store fat in humps, conserve water" },
        { value: 300, prompt: "Daily temp pattern of hot deserts:", answer: "High daytime, rapid nighttime cooling" },
        { value: 400, prompt: "Example of drought-avoidance plant strategy:", answer: "Ephemeral plants completing life cycle after rain" },
        { value: 500, prompt: "Term for water-harvesting surface dew collection:", answer: "Fog harvesting" }
      ]
    },
    {
      name: "Tundra",
      clues: [
        { value: 100, prompt: "Soil condition that restricts deep roots:", answer: "Permafrost" },
        { value: 200, prompt: "Typical vegetation type:", answer: "Low shrubs, mosses, lichens" },
        { value: 300, prompt: "Why are bogs common in tundra?", answer: "Poor drainage over permafrost" },
        { value: 400, prompt: "Animal with seasonal coat change to white:", answer: "Arctic hare / fox / ptarmigan" },
        { value: 500, prompt: "Two key climate traits of tundra:", answer: "Short cool summers; long cold winters" }
      ]
    },
    {
      name: "Grasslands",
      clues: [
        { value: 100, prompt: "North American grassland name:", answer: "Prairie" },
        { value: 200, prompt: "Root adaptation that helps grasses regrow after fire:", answer: "Deep fibrous root systems" },
        { value: 300, prompt: "Main driver keeping trees sparse:", answer: "Periodic fires and grazing" },
        { value: 400, prompt: "Two major grassland soil traits:", answer: "Rich organic layer; good fertility" },
        { value: 500, prompt: "Large grazer shaping prairie ecosystems:", answer: "Bison" }
      ]
    },
    {
      name: "Oceans",
      clues: [
        { value: 100, prompt: "Zone with enough light for photosynthesis:", answer: "Photic / euphotic zone" },
        { value: 200, prompt: "Why coral bleaching happens:", answer: "Loss of symbiotic zooxanthellae under stress" },
        { value: 300, prompt: "Term for nutrient-rich, cold water rising to surface:", answer: "Upwelling" },
        { value: 400, prompt: "One ocean acidification impact on organisms:", answer: "Harder to form calcium carbonate shells" },
        { value: 500, prompt: "Deep sea energy source independent of sunlight:", answer: "Chemosynthesis at hydrothermal vents" }
      ]
    }
  ]
}, null, 2);

export default function LeopardyGames() {
  const { user } = useAuth();
  const { hostGames, playerGames, loading, error } = useLeopardyGames(user);
  const [jsonText, setJsonText] = useState(exampleJson);
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [generateError, setGenerateError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const parsedPreview = useMemo(() => {
    try {
      if (!jsonText.trim()) return null;
      return JSON.parse(jsonText);
    } catch {
      return "invalid";
    }
  }, [jsonText]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError(null);
    if (!user) {
      setCreateError("Please sign in first.");
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== "object") throw new Error("Expected a JSON object.");
      if (!Array.isArray(parsed.categories)) throw new Error("JSON must include categories[].");
    } catch (err) {
      setCreateError(err.message || "Invalid JSON.");
      return;
    }
    setCreating(true);
    try {
      await addDoc(collection(db, "games_leopardy"), {
        title: parsed.title || "Leopardy Game",
        data: parsed,
        hostUid: user.uid,
        hostDisplayName: user.displayName || user.email || "Host",
        playerIds: [],
        status: "draft",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setJsonText("");
    } catch (err) {
      console.error("[LeopardyGames] create", err);
      setCreateError(err.message || "Failed to create game.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (game) => {
    if (!window.confirm(`Delete game "${game.title || game.data?.title || "Leopardy"}"?`)) return;
    setDeletingId(game.id);
    setLocalError(null);
    try {
      await deleteDoc(doc(db, "games_leopardy", game.id));
    } catch (err) {
      console.error("[LeopardyGames] delete", err);
      setLocalError(err.message || "Failed to delete game.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRefresh = async (game) => {
    setRefreshingId(game.id);
    setLocalError(null);
    try {
      await updateDoc(doc(db, "games_leopardy", game.id), {
        updatedAt: serverTimestamp(),
        version: (game.version || 0) + 1,
        state: {},
      });
    } catch (err) {
      console.error("[LeopardyGames] refresh", err);
      setLocalError(err.message || "Failed to refresh game.");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleGenerate = async () => {
    setGenerateError(null);
    if (!promptText.trim()) {
      setGenerateError("Please enter a topic or prompt.");
      return;
    }
    setGenerating(true);
    try {
      const res = await call("generateLeopardyGame", { prompt: promptText });
      const game = res?.data?.game || res?.data;
      if (!game || !game.categories) throw new Error("No game data returned.");
      setJsonText(JSON.stringify(game, null, 2));
    } catch (err) {
      console.error("[LeopardyGames] generate", err);
      const msg = err?.code === "functions/not-found"
        ? "generateLeopardyGame is not deployed (404). Deploy the callable function in Firebase or set the correct region."
        : err.message || "Failed to generate game via GPT.";
      const detail = err?.details?.content ? ` Raw: ${err.details.content.slice(0, 400)}` : "";
      setGenerateError(`${msg}${detail}`);
    } finally {
      setGenerating(false);
    }
  };

  const renderList = (games, role) => {
    if (loading) return <p>Loading…</p>;
    if (!games || games.length === 0) return <p>No games yet.</p>;
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {games.map((g) => (
          <Paper key={g.id} style={paperStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700 }}>{g.title || g.data?.title || "Leopardy Game"}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>ID: {g.id}</div>
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>Role: {role}</div>
            {g.status && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>Status: {g.status}</div>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <Link to={`/leopardy/${role === "host" ? "host" : "play"}/${g.id}`} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #1976d2" }}>
                Open
              </Link>
              <Link to={`/leopardy/view/${g.id}`} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #9c27b0", color: "#9c27b0" }}>
                Class view
              </Link>
              {role === "host" && (
                <>
                  <Button size="small" variant="outlined" onClick={() => handleRefresh(g)} disabled={refreshingId === g.id}>
                    {refreshingId === g.id ? "Refreshing…" : "Refresh"}
                  </Button>
                  <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(g)} disabled={deletingId === g.id}>
                    {deletingId === g.id ? "Deleting…" : "Delete"}
                  </Button>
                </>
              )}
            </div>
          </Paper>
        ))}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 1000, margin: "24px auto", padding: "0 16px", display: "grid", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ margin: 0 }}>Leopardy Games</h1>
        <Link to="/leopardy" style={{ fontSize: 14 }}>Open quick play →</Link>
      </div>

      {!user && (
        <Alert severity="info">Sign in to create and manage Leopardy games.</Alert>
      )}

      {user && (
        <Paper style={paperStyle}>
          <h2 style={{ marginTop: 0 }}>Create a new game</h2>
          <p style={{ marginTop: 4, fontSize: 14 }}>Paste JSON with title, categories, clues, etc. We’ll store it for host/player/class views.</p>
          <form onSubmit={handleCreate} style={{ display: "grid", gap: 12 }}>
            <Stack spacing={1}>
              <TextField
                label="Generate with GPT (topic/prompt)"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="e.g., Grade 10 geometry review with 5 categories and 5 clues each"
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button variant="outlined" onClick={handleGenerate} disabled={generating}>
                  {generating ? "Generating…" : "Generate with GPT"}
                </Button>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {promptIdeas.map((idea) => (
                    <Button key={idea} size="small" variant="text" onClick={() => setPromptText(idea)}>{idea}</Button>
                  ))}
                </Stack>
              </Stack>
              {generateError && <Alert severity="error">{generateError}</Alert>}
            </Stack>
            <TextField
              label="Game JSON"
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              multiline
              minRows={6}
              placeholder='{"title": "My Leopardy", "categories": [...]}'
            />
            {parsedPreview && parsedPreview !== "invalid" && (
              <Alert severity="success">Looks good. Title: {parsedPreview.title || "Untitled"}</Alert>
            )}
            {parsedPreview === "invalid" && (
              <Alert severity="warning">JSON is invalid. Please fix before creating.</Alert>
            )}
            {createError && <Alert severity="error">{createError}</Alert>}
            <Button
              type="submit"
              variant="contained"
              disabled={creating || parsedPreview === "invalid" || !jsonText.trim()}
            >
              {creating ? "Creating…" : "Create game"}
            </Button>
          </form>
        </Paper>
      )}

      {(error || localError) && <Alert severity="error">{error || localError}</Alert>}

      <Paper style={paperStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Host</h2>
          <span style={{ fontSize: 13, opacity: 0.7 }}>Games where you are the host/judge</span>
        </div>
        <Divider style={{ margin: "12px 0" }} />
        {renderList(hostGames, "host")}
      </Paper>

      <Paper style={paperStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Player</h2>
          <span style={{ fontSize: 13, opacity: 0.7 }}>Games you’ve been added to</span>
        </div>
        <Divider style={{ margin: "12px 0" }} />
        {renderList(playerGames, "player")}
      </Paper>
    </div>
  );
}
